<template>
    <edit-resource
        :form="form"
        :title="title"
        @close="$emit('close')"
        @submit="saveApplication"
    >

        <div class="w-768 space-y-32">

            <div class="space-y-12">
                <h2 class="text-body font-semibold capitalize">{{ $t('applications.personal_data') }}</h2>
                <div class="grid grid-cols-3 grid-rows-3 grid-flow-col gap-16">
                    <simple-input autofocus v-model="form.first_name" :input-label="$t('applications.first_name')" />
                    <simple-input v-model="form.last_name" :input-label="$t('applications.last_name')" />
                    <date-picker v-model="form.birthdate" :input-label="$t('applications.birthdate')" />
                    <simple-input v-model="form.rijksregister_nr" :input-label="$t('applications.ssn')" />
                    <simple-input v-model="form.iban_nr" :input-label="$t('applications.iban')" />
                    <simple-input v-model="form.salary_expectation" :input-label="$t('applications.salary_expectation')" />
                    <siu
                        v-model="form.photo_path_queued_id"
                        :image="application ? application.image_url : null"
                        class="row-span-full"
                        classes="h-full"
                        is-small
                    ></siu>
                </div>
            </div>

            <div class="space-y-12">
                <h2 class="text-body font-semibold capitalize">{{ $t('applications.contact_data') }}</h2>
                <div class="grid grid-cols-3 grid-rows-3 grid-flow-col gap-16">
                    <select-dropdown
                        :input-label="$t('general.language')"
                        :options="getCurrentlyActiveJobsiteLanguages"
                        v-model="form.locale"
                    />
                    <simple-input v-model="form.email" :input-label="$t('applications.email')" />
                    <simple-input v-model="form.phone_number" :input-label="$t('applications.phone')" />
                    <simple-input class="col-span-2" v-model="form.street" :input-label="$t('applications.street')" />
                    <simple-input v-model="form.house_number" :input-label="$t('applications.house_number')" />
                    <simple-input v-model="form.postal_code" :input-label="$t('applications.postal_code')" />
                    <simple-input v-model="form.city" :input-label="$t('applications.city')" />
                    <select-dropdown
                        :autoTranslate="false"
                        :input-label="$t('applications.country')"
                        :options="countries"
                        searchable
                        v-model="form.country"
                    />
                </div>
            </div>

            <div class="space-y-12">
                <div class="flex items-center gap-12">
                    <h2 class="text-body font-semibold capitalize">{{ $t('applications.source') }}</h2>

                    <more-info-button
                        only-use-icon
                        hide-label
                        position="left"
                        v-if="!isInterim"
                    >
                        <template #content>
                            <b>{{ $t('general.more_info') }}</b>
                            <p v-html="$t('applications.source_info')"></p>
                        </template>
                    </more-info-button>
                </div>

                <div>
                    <span v-if="isInterim" class="flex text-neutral-grey text-body-sm mb-8">{{ $t('applications.source_is_interim') }}</span>
                    <div v-if="fetching">
                        <loading size="small"></loading>
                    </div>

                    <div v-else-if="!isInterim" class="grid grid-cols-2 gap-16">
                        <select-dropdown
                            add-new
                            :allowSpaces="true"
                            :auto-translate="false"
                            class="flex flex-wrap"
                            :options="referrals"
                            :placeholder="placeholder"
                            searchable
                            v-model="form.referrer_source"
                            @new="addNew"
                        >
                            <template #after>
                                <tooltip
                                    :content="$t('settings.reset_referer_source')"
                                    class="-mt-8"
                                    position="right"
                                >
                                    <button
                                        class="flex text-primary-80 ml-8"
                                        @click="resetSource"
                                    >
                                        <svg-icon class="max-w-14 max-h-14 opacity-75"
                                                  type="replay"></svg-icon>
                                    </button>
                                </tooltip>
                            </template>
                            <template>
                                {{ $t('applications.source_name') }}
                            </template>
                        </select-dropdown>

                        <simple-input
                            v-model="form.referrer"
                            placeholder="https://www.linkedin.com/company/jobtoolz/"
                            @keydown.space.prevent
                        >
                            {{ $t('applications.source_description') }}
                        </simple-input>
                    </div>
                    <div v-else>
                        <simple-input :value="interimName" disabled />
                    </div>
                </div>
            </div>

            <div class="space-y-12">
                <h2 class="text-body font-semibold capitalize mb-24">{{ $t('settings.company.social') }}</h2>
                <simple-input v-model="form.linkedin" class="mb-16">{{ $t('jobs.field_names.linkedin') }}</simple-input>
            </div>

            <div class="space-y-12">
                <h2 class="text-body font-semibold capitalize mb-24">{{ $t('applications.motivation') }}</h2>

                <simple-textarea v-model="form.motivation"></simple-textarea>
            </div>

            <div v-if="!application" class="space-y-12">
                <h2 class="text-body font-semibold capitalize mb-24">{{ $t('applications.upload_resume') }}</h2>
                <file-upload v-model="form.cv"/>
            </div>

            <checkbox v-if="!application" v-model="form.processing_permission">
                {{ $t('applications.processing_permission') }}
            </checkbox>

        </div>

    </edit-resource>
</template>

<script>
import EditResource from './EditResource.vue';
import Input from '../forms/Input.vue';
import Form from '../../etc/forms/Form';
import SingleImageUpload from '../forms/SingleImageUpload.vue';
import DatePicker from '../forms/DatePicker.vue';
import * as applications from '../../services/ApplicationService';
import Application from '../../models/Application';
import {normalizeJson, getCountriesSelectOptions} from '../../../shared/helpers';
import moment from 'moment';
import TextArea from '../forms/TextArea.vue';
import Stage from '../../models/Stage';
import SelectDropdown from '../forms/SelectDropdown.vue';
import FileUpload from '../forms/FileUpload';
import Checkbox from '../forms/Checkbox.vue';
import MoreInfoButton from './MoreInfoButton.vue';
import Loading from './Loading';
import SvgIcon from './SvgIcon.vue';
import Tooltip from "../navigation/Tooltip";
import {mapStores} from "pinia";
import {useMainStore} from "../../state/store";
import {useRepo} from 'pinia-orm';

function getFields() {
    return [
        {name: 'first_name', value: '', rules: ['required']},
        {name: 'last_name', value: '', rules: ['required']},
        {name: 'email', value: ''},
        {name: 'birthdate', value: null, accessor: value => value ? moment.utc(value) : null},
        {name: 'phone_number', value: ''},
        {name: 'postal_code', value: ''},
        {name: 'street', value: ''},
        {name: 'house_number', value: ''},
        {name: 'rijksregister_nr', value: ''},
        {name: 'iban_nr', value: ''},
        {name: 'city', value: ''},
        {name: 'country', value: null},
        {name: 'photo_path_queued_id', value: null},
        {name: 'motivation', value: ''},
        {name: 'locale', value: null},
        {name: 'cv', value: null},
        {name: 'processing_permission', value: false},
        {name: 'linkedin', value: null},
        {name: 'salary_expectation', value: ''},
        {name: 'referrer', value: ''},
        {name: 'referrer_source', value: ''},
    ];
}

export default {
    components: {
        Loading,
        Tooltip,
        MoreInfoButton,
        SvgIcon,
        'edit-resource': EditResource,
        'simple-input': Input,
        'siu': SingleImageUpload,
        'date-picker': DatePicker,
        'simple-textarea': TextArea,
        'select-dropdown': SelectDropdown,
        'file-upload': FileUpload,
        'checkbox': Checkbox,
    },
    props: {
        application: {
            type: Application,
        },
        stage: {
            type: Stage,
            required: true,
        },
    },
    setup() {
        const mainStore = useMainStore();
        return { mainStore };
    },
    data: function () {
        return {
            form: this.generateForm(),
            savingTemplate: false,
            countries: getCountriesSelectOptions(this.$i18n),
            fetching: false,
            referrals: [],
        };
    },
    computed: {
        availableLanguages() {
            return this.mainStore.availableLanguages;
        },
        locale() {
            return this.mainStore.locale;
        },
        currentCompany() {
            return this.mainStore.currentCompany;
        },
        title() {
            return this.$t(this.application ? 'general.edit_subject' : 'general.add_subject', {subject: this.$t('applications.applicant')});
        },
        isInterim() {
            return !!(this.application && this.application.interim_candidate_id);
        },
        placeholder() {
            return this.isInterim ? 'interim' : this.$t('applications.source_not_linked');
        },
        getCurrentlyActiveJobsiteLanguages() {
            return this.currentCompany.jobsite.active_languages.map(language => {
                return {
                    label: "languages." + language + ".title",
                    value: language
                }
            });
        },
        interimName() {
            return this.application.interim?.name || this.$t('applications.source_not_found')
        }
    },
    methods: {
        resetSource() {
            this.form.referrer_source = null;
        },
        generateForm() {
            let fields = getFields();

            if (this.application) {
                fields.forEach((field, index) => {
                    fields[index].value = JSON.parse(JSON.stringify(this.application[field.name] || null));
                });
            } else {

                fields[fields.findIndex(entry => entry.name == 'processing_permission')].rules = ['required'];


                fields[fields.findIndex(entry => entry.name == 'country')].value = this.mainStore.currentCompany.country || 'BE';

                //set locale
                let index = Object.keys(fields).find(key => fields[key]['name'] === 'locale');

                fields[index].value = this.mainStore.locale;
            }

            return new Form(fields);
        },
        async saveApplication() {

            if (this.application && this.application.id) {
                await this.updateApplication();
            } else {
                await this.createApplication();
            }

            this.$emit('close');
        },
        createApplication() {
            return this.form.submit(data => {
                data.relevant_info = data.motivation;
                let processing_permission = data.processing_permission;
                delete data.motivation;
                delete data.processing_permission;
                return applications.store({
                    data: {
                        processing_permission,
                        attributes: data,
                        relationships: {stage: {data: {id: this.stage.id}}},
                    },
                });
            }).then(result => {
                return useRepo(Application).save(normalizeJson(result));
            });
        },
        updateApplication() {
            return this.form.submit(data => {
                data.relevant_info = data.motivation;
                delete data.motivation;
                return applications.update(this.application.id, {
                    data: {attributes: data},
                });
            }).then(result => {
                return useRepo(Application).save(normalizeJson(result));
            });
        },
        fetchSources() {
            if (this.isInterim) {
                return;
            }

            this.fetching = true;

            applications.sources(true).then(result => {
                this.referrals = result.data.map(val => ({
                    value: val.referrer_source,
                    label: val.referrer_source,
                }));

                this.fetching = false;
            });
        },
        addNew(value) {
            this.referrals.push({
                label: value,
                value: value,
            });
        },
    },
    mounted() {
        this.fetchSources();
    },
};
</script>
